import { useT } from '../i18n/locale-context';
import { SaveSearchButton } from './save-search-button';
import { BellIcon } from './icons';

interface NoResultsAlertProps {
  /** The keyword that returned nothing — the whole reason this is shown. */
  q: string;
  region?: string;
  category?: string;
  ikpu?: string;
  nicheSlug?: string;
  scope?: string;
}

/**
 * The empty state for a keyword search that matched nothing.
 *
 * ## Why this is not just a nicer "no results" message
 *
 * A search returning nothing is usually not a broken search — the catalogue
 * holds ~2 400 live lots at a time, and a real, specific demand simply is not
 * being tendered TODAY. Measured on the 107-query relevance set, 14 queries
 * match fewer than five live lots, and for several of them (`тостер`,
 * `Фальшпол`, `bitonlash`) no relevant lot exists at any depth. Every ranking
 * lever was tried against those queries and none of them can help: there is
 * nothing to rank.
 *
 * What the user actually wants is to be told WHEN it appears — which is the
 * product's own paid loop, and until now the one moment we were guaranteed to
 * waste. An empty page is the point of highest intent and lowest cost to ask:
 * they have just typed exactly what they want, and there is nothing else on
 * screen competing for the click.
 *
 * The button is the ordinary {@link SaveSearchButton}, not a special path, so
 * signed-out users get the same stash-and-redirect flow, the free-tier
 * saved-search cap still applies, and the alert is created by the same code
 * that creates every other one. `matchCount={0}` is passed honestly — it makes
 * the button show its own "these filters match nothing right now" warning,
 * which here is the entire premise rather than a caveat.
 */
export function NoResultsAlert({
  q,
  region,
  category,
  ikpu,
  nicheSlug,
  scope,
}: NoResultsAlertProps) {
  const t = useT();
  return (
    <div className="rounded-lg border border-edge bg-bone-surface p-8 text-center shadow-m-sm">
      <BellIcon className="mx-auto h-6 w-6 text-ink-muted" />
      <p className="mt-3 text-[15px] font-medium text-ink">{t('search.empty.title')}</p>
      <p className="mx-auto mt-1 max-w-md text-[13px] leading-relaxed text-ink-muted">
        {t('search.empty.body')}
      </p>
      <div className="mt-4 flex justify-center">
        <SaveSearchButton
          q={q}
          region={region}
          category={category}
          ikpu={ikpu}
          nicheSlug={nicheSlug}
          scope={scope}
          highlight
          matchCount={0}
        />
      </div>
    </div>
  );
}
